import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  ComposedModal,
  ContentSwitcher,
  InlineLoading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Search,
  Select,
  SelectItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  TextInput,
} from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { api, Facility, Mapping, MappingKind, Permission } from '../lib/api';
import { DEFAULT_PAGE_SIZE, lastPageIndex } from '../lib/pagination';
import { Card, TablePagination, useToast } from '../components/ui';
import { ImportMappingsModal } from './ImportMappingsModal';

const KINDS: Array<{ value: MappingKind; label: string; blurb: string }> = [
  { value: 'element', label: 'Data elements', blurb: 'Indicator codes to DHIS2 data elements' },
  { value: 'combo', label: 'Disaggregations', blurb: 'Age/sex and ward metrics to category option combos' },
  { value: 'dataset', label: 'Data sets', blurb: 'Report codes to DHIS2 data sets' },
];

const DHIS2_UID = /^[A-Za-z][A-Za-z0-9]{10}$/;

/**
 * The screen that replaces editing UIDs in code. Facilities send stable source codes; these rows
 * decide what each one becomes in DHIS2. A row scoped to a facility overrides the global row for
 * that facility only, so one site can pilot a change without moving everybody.
 *
 * A national data set's worth of element mappings runs to a few hundred rows after a bulk import,
 * so this page pages through the server rather than rendering everything at once.
 */
export function Mappings({ can }: { can: (permission: Permission) => boolean }) {
  const [kind, setKind] = useState<MappingKind>('element');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState<Mapping[]>([]);
  const [total, setTotal] = useState(0);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [unmapped, setUnmapped] = useState<Array<{ kind: MappingKind; sourceCode: string; occurrences: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Mapping | { sourceCode: string; kind: MappingKind } | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const latestRequest = useRef(0);
  const toast = useToast();

  const isAdmin = can('mappings.write');

  // Kind, search and page all change what this page of rows is, so a change to any of them
  // reloads just the table. Facilities and the unmapped-codes banner are independent of that -
  // they load once and after a mutation, not on every keystroke or page flip.
  const loadMappings = useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        kind,
        limit: String(pageSize),
        offset: String(page * pageSize),
      });
      if (search.trim()) {
        params.set('search', search.trim());
      }
      const result = await api.get<{ rows: Mapping[]; total: number }>(`/api/mappings?${params.toString()}`);
      // Typing in the search box fires a request per keystroke; only the newest may land.
      if (request !== latestRequest.current) return;
      setTotal(result.total);
      const lastPage = lastPageIndex(result.total, pageSize);
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setRows(result.rows);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, [kind, page, pageSize, search, toast]);

  const loadSupportingData = useCallback(async () => {
    try {
      const [facilityList, unmappedCodes] = await Promise.all([
        api.get<Facility[]>('/api/facilities'),
        api.get<Array<{ kind: MappingKind; sourceCode: string; occurrences: number }>>(
          '/api/mappings/unmapped',
        ),
      ]);
      setFacilities(facilityList);
      setUnmapped(unmappedCodes);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    }
  }, [toast]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  useEffect(() => {
    loadSupportingData();
  }, [loadSupportingData]);

  /** After anything that can change what is mapped or what is missing a mapping. */
  const refresh = useCallback(
    () => Promise.all([loadMappings(), loadSupportingData()]),
    [loadMappings, loadSupportingData],
  );

  const missingForKind = useMemo(() => unmapped.filter((row) => row.kind === kind), [unmapped, kind]);

  async function removeMapping(mapping: Mapping) {
    if (!confirm(`Delete the mapping for ${mapping.sourceCode}? Its values stop being sent to DHIS2.`)) {
      return;
    }
    try {
      await api.del(`/api/mappings/${mapping.id}`);
      toast('ok', 'Mapping deleted');
      await refresh();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    }
  }

  return (
    <>
      <div className="mappings-toolbar">
        <div className="mappings-toolbar-left">
          <ContentSwitcher
            className="mappings-kind-switcher"
            selectedIndex={KINDS.findIndex((k) => k.value === kind)}
            onChange={({ index }) => {
              if (typeof index === 'number' && KINDS[index]) {
                setKind(KINDS[index].value);
                setPage(0);
              }
            }}
            aria-label="Mapping kind switcher"
          >
            {KINDS.map((option) => (
              <Switch key={option.value} name={option.value} text={option.label} />
            ))}
          </ContentSwitcher>
          <span className="mappings-toolbar-helper">
            {KINDS.find((option) => option.value === kind)?.blurb}
          </span>
          <Search
            id="mappings-search"
            labelText="Search source code or label"
            placeholder="Search source code or label"
            size="md"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            onClear={() => {
              setSearch('');
              setPage(0);
            }}
            className="mappings-search-input"
          />
        </div>

        {isAdmin ? (
          <div className="row" style={{ gap: 8 }}>
            <Button kind="tertiary" size="md" onClick={() => setImporting(true)}>
              Import mappings…
            </Button>
            <Button
              kind="primary"
              size="md"
              renderIcon={Add}
              onClick={() => setEditing('new')}
            >
              Add mapping
            </Button>
          </div>
        ) : null}
      </div>

      {missingForKind.length > 0 ? (
        <div className="banner banner-warn">
          <div className="stack" style={{ gap: 8, width: '100%' }}>
            <span>
              <strong>{missingForKind.length}</strong> code
              {missingForKind.length === 1 ? '' : 's'} arrived from facilities with no mapping. Values
              carrying {missingForKind.length === 1 ? 'it' : 'them'} were not sent to DHIS2.
            </span>
            <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {missingForKind.map((row) => (
                <Button
                  key={row.sourceCode}
                  kind="tertiary"
                  size="sm"
                  onClick={() => setEditing({ sourceCode: row.sourceCode, kind })}
                  disabled={!isAdmin}
                  title={`Seen in ${row.occurrences} report(s)`}
                >
                  Map {row.sourceCode}
                </Button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <Card bodyless>
        <div className="mappings-results-panel">
          {loading && rows.length === 0 ? (
            <div className="mappings-results-empty">
              <InlineLoading description="Loading mappings…" />
            </div>
          ) : rows.length === 0 ? (
            <div className="mappings-results-empty">
              <div className="mappings-empty-title">
                {search.trim() ? 'No mappings match' : 'No mappings of this kind'}
              </div>
              <div className="mappings-empty-desc">
                {search.trim()
                  ? 'Nothing has that source code or label. Clear the search to see them all.'
                  : 'Add one to tell the hub which DHIS2 identifier each source code becomes.'}
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <Table size="sm">
                <TableHead>
                  <TableRow>
                    <TableHeader>Source code</TableHeader>
                    <TableHeader>DHIS2 UID</TableHeader>
                    <TableHeader>Label</TableHeader>
                    <TableHeader>Scope</TableHeader>
                    <TableHeader>State</TableHeader>
                    <TableHeader />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((mapping) => (
                    <TableRow key={mapping.id}>
                      <TableCell className="mono">{mapping.sourceCode}</TableCell>
                      <TableCell className="mono" style={{ color: 'var(--accent-bright)' }}>
                        {mapping.dhis2Uid}
                      </TableCell>
                      <TableCell className="muted">{mapping.name ?? '—'}</TableCell>
                      <TableCell>
                        {mapping.facilityCode ? (
                          <Tag type="cyan" size="sm" className="mapping-status-tag">
                            {mapping.facilityCode}
                          </Tag>
                        ) : (
                          <span className="faint small">Global</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {mapping.enabled ? (
                          <Tag type="green" size="sm" className="mapping-status-tag">
                            Active
                          </Tag>
                        ) : (
                          <Tag type="gray" size="sm" className="mapping-status-tag">
                            Off
                          </Tag>
                        )}
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                            <Button
                              kind="ghost"
                              size="sm"
                              onClick={() => setEditing(mapping)}
                            >
                              Edit
                            </Button>
                            <Button
                              kind="danger--ghost"
                              size="sm"
                              onClick={() => removeMapping(mapping)}
                            >
                              Delete
                            </Button>
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {rows.length > 0 ? (
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalItems={total}
              onChange={(nextPage, nextPageSize) => {
                setPage(nextPage);
                setPageSize(nextPageSize);
              }}
            />
          ) : null}
        </div>
      </Card>

      {editing ? (
        <MappingModal
          initial={editing === 'new' ? { kind } : editing}
          facilities={facilities}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}

      {importing ? (
        <ImportMappingsModal
          onClose={() => setImporting(false)}
          onImported={() => {
            void refresh();
          }}
        />
      ) : null}
    </>
  );
}

function MappingModal({
  initial,
  facilities,
  onClose,
  onSaved,
}: {
  initial: Mapping | { sourceCode?: string; kind: MappingKind };
  facilities: Facility[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const existing = 'id' in initial ? initial : null;
  const [kind, setKind] = useState<MappingKind>(initial.kind);
  const [sourceCode, setSourceCode] = useState(initial.sourceCode ?? '');
  const [dhis2Uid, setDhis2Uid] = useState(existing?.dhis2Uid ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [facilityId, setFacilityId] = useState(existing?.facilityId ? String(existing.facilityId) : '');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const uidValid = DHIS2_UID.test(dhis2Uid.trim());

  async function save() {
    setBusy(true);
    try {
      if (existing) {
        await api.patch(`/api/mappings/${existing.id}`, {
          sourceCode: sourceCode.trim(),
          dhis2Uid: dhis2Uid.trim(),
          name: name.trim() || null,
          enabled,
        });
      } else {
        await api.post('/api/mappings', {
          kind,
          sourceCode: sourceCode.trim(),
          dhis2Uid: dhis2Uid.trim(),
          name: name.trim() || null,
          facilityId: facilityId ? Number(facilityId) : null,
          enabled,
        });
      }
      toast('ok', `Mapping for ${sourceCode.trim()} saved`);
      onSaved();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ComposedModal open onClose={onClose} size="sm" className="mappings-modal">
      <ModalHeader
        title={existing ? `Edit ${existing.sourceCode}` : 'Add mapping'}
        closeModal={onClose}
      />
      <ModalBody>
        <div className="mappings-modal-form">
          {!existing ? (
            <Select
              id="mapping-kind-select"
              labelText="Kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as MappingKind)}
            >
              {KINDS.map((option) => (
                <SelectItem key={option.value} value={option.value} text={option.label} />
              ))}
            </Select>
          ) : null}

          <TextInput
            id="mapping-source-code-input"
            labelText="Source code"
            helperText="The code facilities send, e.g. OPD_NEW_ATTENDANCE or MALE_UNDER_5."
            placeholder="OPD_NEW_ATTENDANCE"
            value={sourceCode}
            onChange={(event) => setSourceCode(event.target.value)}
            className="mapping-input-mono"
          />

          <TextInput
            id="mapping-dhis2-uid-input"
            labelText="DHIS2 UID"
            helperText={
              uidValid || dhis2Uid === ''
                ? 'The identifier in DHIS2 this code becomes.'
                : undefined
            }
            invalid={!uidValid && dhis2Uid !== ''}
            invalidText="A DHIS2 UID is 11 characters and starts with a letter."
            placeholder="zeKbKiVtV9Q"
            value={dhis2Uid}
            onChange={(event) => setDhis2Uid(event.target.value)}
            className="mapping-input-mono"
          />

          <TextInput
            id="mapping-label-input"
            labelText="Label"
            helperText="Shown in this dashboard only; DHIS2 keeps its own name."
            placeholder="New Attendance"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          {!existing ? (
            <Select
              id="mapping-scope-select"
              labelText="Scope"
              helperText="A facility-scoped row overrides the global one for that facility only."
              value={facilityId}
              onChange={(event) => setFacilityId(event.target.value)}
            >
              <SelectItem value="" text="Global — applies to every facility" />
              {facilities.map((facility) => (
                <SelectItem
                  key={facility.id}
                  value={String(facility.id)}
                  text={`Only ${facility.name}`}
                />
              ))}
            </Select>
          ) : null}

          <Checkbox
            id="mapping-enabled-checkbox"
            labelText="Active"
            checked={enabled}
            onChange={(_event, { checked }) => setEnabled(checked)}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={save}
          disabled={busy || !sourceCode.trim() || !uidValid}
        >
          {busy ? <InlineLoading status="active" description="" /> : null}
          Save
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}
